// Editable Client Standards for the Billing Review detail. Inline view shows a
// read-only summary; clicking "Edit" opens a modal with the full table plus
// add/edit/delete controls.

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../components/Modal';
import { useAuth } from '../../auth/AuthContext';
import PendingTag from './PendingTag';
import {
  submitChange, wasWithdrawn, withPending, withoutPending, pendingForKey, revertsPending,
} from './billingChangeApi';

function pct(n) { return n == null ? '—' : `${n}%`; }

export default function ClientStandardsEditor({
  client, period, readOnly, availableServices = [], onOptimisticUpdate, onChanged, onWithdraw,
}) {
  const { email } = useAuth();
  const floors = client.officeFloors || {};
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null); // serv being edited
  const [editValue, setEditValue]   = useState('');
  const [addingNew, setAddingNew]   = useState(false);
  const [newServ, setNewServ]       = useState('');
  const [newValue, setNewValue]     = useState('');
  const [saving, setSaving]         = useState(false);

  const standards = client.clientStandards || [];
  // Staged changes waiting on the Billing Team (client.pending is set by the server).
  const pendingStds = (client.pending || []).filter(p => p.section === 'client_standard');
  const pendingByServ = Object.fromEntries(pendingStds.map(p => [String(p.fieldKey).replace(/^std:/, ''), p]));

  // Services already assigned to this client (can't be added twice).
  const usedServs = new Set(standards.map(s => s.service));
  // 'ALL' is the firm-wide default standard (most standards are ALL rows). It isn't a real
  // PE service so the services list never contains it — it is always pinned first, like Botfield
  // does (and marked once the client already has one).
  const pickableServices = [
    'ALL',
    ...availableServices.filter(s => s !== 'ALL' && !usedServs.has(s)),
  ];

  function floorFor(serv) {
    return floors[serv] ?? floors.ALL ?? null;
  }

  // Same rules the server enforces (0–100, not below the office floor). Checked
  // before save so a bad value never leaves the input; the `min` attribute alone
  // doesn't stop typed values.
  function validate(serv, value) {
    if (value === '' || value == null) return 'Enter a value.';
    const n = Number(value);
    if (!Number.isFinite(n)) return 'Value must be a number.';
    if (n < 0 || n > 100) return 'Value must be between 0 and 100.';
    const floor = floorFor(String(serv || '').toUpperCase());
    if (floor != null && n < floor) return `Can't be below the office floor of ${floor}%.`;
    return null;
  }

  function closeModal() {
    if (saving) return;
    setEditing(null);
    setEditValue('');
    setAddingNew(false);
    setNewServ('');
    setNewValue('');
    setModalOpen(false);
  }

  // ── Standards CRUD ───────────────────────────────────────────────────────

  async function save(serv, value) {
    const servUp   = String(serv).toUpperCase();
    const valueNum = Number(value);
    const invalid  = validate(servUp, value);
    if (invalid) { toast.error(`${servUp}: ${invalid}`); return; }
    const snapshot = standards;
    const snapshotPending = client.pending || [];
    const idx = snapshot.findIndex(s => s.service === servUp);
    const nextStandards = idx === -1
      ? [...snapshot, { service: servUp, value: valueNum }]
      : snapshot.map((s, i) => i === idx ? { ...s, value: valueNum } : s);
    const body = { section: 'client_standard', payload: { service: servUp, value: valueNum } };
    // Setting it back to the live value just takes the open request back.
    const undo = revertsPending(pendingForKey(client, body.section, null, body.payload), body, email);

    onOptimisticUpdate?.(c => {
      const next = { ...c, clientStandards: nextStandards };
      return undo
        ? withoutPending(next, body.section, null, body.payload)
        : withPending(next, { ...body, summary: `Client standard ${servUp}: → ${valueNum}%`, email });
    });
    setEditing(null);
    setAddingNew(false);
    setNewServ(''); setNewValue(''); setEditValue('');
    setSaving(true);

    try {
      const out = await submitChange({ period, contIndex: client.contIndex, ...body });
      toast.success(wasWithdrawn(out)
        ? `${servUp} standard change withdrawn.`
        : `${servUp} standard submitted for Billing Team approval.`);
      onChanged?.();
    } catch (err) {
      onOptimisticUpdate?.(c => ({ ...c, clientStandards: snapshot, pending: snapshotPending }));
      toast.error(`Save failed: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSaving(false);
    }
  }

  async function remove(serv) {
    const body = { section: 'client_standard', op: 'delete', payload: { service: serv } };
    const pend = pendingForKey(client, body.section, null, body.payload);
    // Deleting a standard that only exists as an open request is a withdraw — no confirm, nothing
    // for the Billing Team to approve.
    const undo = revertsPending(pend, body, email);
    if (!undo && !window.confirm(`Remove client standard "${serv}" for ${client.clientCode}?`)) return;
    const snapshot = standards;
    const snapshotPending = client.pending || [];
    const nextStandards = snapshot.filter(s => s.service !== serv);

    onOptimisticUpdate?.(c => {
      const next = { ...c, clientStandards: nextStandards };
      return undo
        ? withoutPending(next, body.section, null, body.payload)
        : withPending(next, { ...body, summary: `Remove client standard ${serv}`, email });
    });
    setSaving(true);

    try {
      const out = await submitChange({ period, contIndex: client.contIndex, ...body });
      toast.success(wasWithdrawn(out)
        ? `${serv} standard withdrawn.`
        : `Removal of ${serv} submitted for Billing Team approval.`);
      onChanged?.();
    } catch (err) {
      onOptimisticUpdate?.(c => ({ ...c, clientStandards: snapshot, pending: snapshotPending }));
      toast.error(`Delete failed: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSaving(false);
    }
  }

  // ── Inline summary ───────────────────────────────────────────────────────

  const summary = (
    <div className="cs-editor">
      {standards.length === 0 ? (
        <div className="detail-empty">No client standards</div>
      ) : (
        <table className="detail-table cs-editor-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Value</th>
              <th>Floor</th>
            </tr>
          </thead>
          <tbody>
            {standards.map(s => {
              const floor = floorFor(s.service);
              return (
                <tr key={s.service}>
                  <td>{s.service}</td>
                  <td>{pct(s.value)}</td>
                  <td className="cs-floor-cell">{floor != null ? pct(floor) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {pendingStds.map(p => (
        <div key={p.changeId} className="br-pending-row">
          <PendingTag change={p} onWithdraw={onWithdraw} />
          <span className="br-sec-note">{p.summary}</span>
        </div>
      ))}

      {!readOnly && (
        <button className="cs-btn cs-btn--add" onClick={() => setModalOpen(true)}>
          <i className="fas fa-edit" /> Edit Client Standards
        </button>
      )}
    </div>
  );

  // ── Edit table (rendered inside modal) ────────────────────────────────────

  const newErr = addingNew && newServ && newValue !== '' ? validate(newServ, newValue) : null;

  const editTable = (
    <>
      {standards.length > 0 || addingNew ? (
        <table className="detail-table cs-editor-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Value</th>
              <th>Floor</th>
              <th className="cs-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {standards.map(s => {
              const isEditing = editing === s.service;
              const floor = floorFor(s.service);
              const editErr = isEditing ? validate(s.service, editValue) : null;
              return (
                <tr key={s.service}>
                  <td>{s.service}<PendingTag change={pendingByServ[s.service]} onWithdraw={onWithdraw} /></td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        className="cs-input"
                        min={floor ?? 0} max={100}
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        autoFocus
                      />
                    ) : pct(s.value)}
                    {editErr && <div className="cs-field-error">{editErr}</div>}
                  </td>
                  <td className="cs-floor-cell">{floor != null ? pct(floor) : '—'}</td>
                  <td className="cs-actions-col">
                    {isEditing ? (
                      <>
                        <button className="cs-btn cs-btn--save" disabled={saving || !!editErr}
                                onClick={() => save(s.service, editValue)}>Save</button>
                        <button className="cs-btn" disabled={saving}
                                onClick={() => { setEditing(null); setEditValue(''); }}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="cs-btn" disabled={saving}
                                onClick={() => { setEditing(s.service); setEditValue(String(s.value)); }}>Edit</button>
                        <button className="cs-btn cs-btn--danger" disabled={saving}
                                onClick={() => remove(s.service)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {addingNew && (
              <tr className="cs-new-row">
                <td>
                  {pickableServices.length > 0 ? (
                    <select
                      className="cs-input"
                      value={newServ}
                      onChange={e => setNewServ(e.target.value)}
                      autoFocus
                    >
                      <option value="">Select service…</option>
                      {pickableServices.map(s => (
                        <option key={s} value={s} disabled={usedServs.has(s)}>
                          {usedServs.has(s) ? `${s} (already set)` : s}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="cs-input"
                      placeholder="e.g. TAX"
                      value={newServ}
                      onChange={e => setNewServ(e.target.value.toUpperCase())}
                      autoFocus
                    />
                  )}
                </td>
                <td>
                  <input
                    type="number"
                    className="cs-input"
                    min={floorFor(newServ.toUpperCase()) ?? 0} max={100}
                    placeholder="%"
                    value={newValue}
                    onChange={e => setNewValue(e.target.value)}
                  />
                  {newErr && <div className="cs-field-error">{newErr}</div>}
                </td>
                <td className="cs-floor-cell">
                  {floorFor(newServ.toUpperCase()) != null ? pct(floorFor(newServ.toUpperCase())) : '—'}
                </td>
                <td className="cs-actions-col">
                  <button className="cs-btn cs-btn--save" disabled={saving || !newServ || newValue === '' || !!newErr}
                          onClick={() => save(newServ, newValue)}>Add</button>
                  <button className="cs-btn" disabled={saving}
                          onClick={() => { setAddingNew(false); setNewServ(''); setNewValue(''); }}>Cancel</button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      ) : (
        <div className="detail-empty">No client standards configured yet.</div>
      )}

      {!addingNew && (
        <button
          className="cs-btn cs-btn--add"
          onClick={() => setAddingNew(true)}
          disabled={saving}
        >
          <i className="fas fa-plus" /> Add Standard
        </button>
      )}
    </>
  );

  return (
    <>
      {summary}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={`Edit Client Standards — ${client.clientCode}`}
        wide
        footer={
          <button className="cs-btn" onClick={closeModal} disabled={saving}>
            Done
          </button>
        }
      >
        {editTable}
      </Modal>
    </>
  );
}
